import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Icon from "@ats/src/components/shared/Icon";
import SmallButton from "@ats/src/components/shared/SmallButton";
import ManageBillingActions from "@ats/src/views/accountAdmin/accountBilling/ManageBillingActions";
import Tooltip from "@ats/src/components/shared/Tooltip";
import { trackEvent } from "@shared/lib/posthog";
import {
  Badge,
  SavingsBadge,
  PricingPrice,
  PricingTitle,
  Features,
  Feature,
  FeatureContent,
  FeatureName,
  FeatureValue,
  ButtonsContainer,
  PlanCardWrapper,
  ContentContainer,
  SavingsIcon,
} from "@ats/src/views/accountAdmin/accountBilling/AccountBillingComponents";

interface Plan {
  name: string;
  price: number;
  features: Array<{
    name: string;
    description?:
      | string
      | Array<{
          name: string;
          description: string;
          tooltipText: string;
        }>;
    included: boolean;
    value?: string;
    includeTooltip?: boolean;
    gateKey?: string;
    isLimitation?: boolean;
    tooltipText?: string;
    limitations?: Array<{
      name: string;
      description: string;
      tooltipText: string;
    }>;
  }>;
  key: string;
  priceId: string;
  lookupKey: string;
  buttonText: string;
  buttonType?: "primary" | "secondary";
  isMostPopular?: boolean;
}

interface PlanCardProps {
  plan: Plan;
  billingPeriod: "monthly" | "yearly";
  isCurrentPlan: boolean;
  currentPlanLookupKey?: string | null;
  currentPlanBillingPeriod?: "monthly" | "yearly" | null;
  hasActiveSubscription: boolean;
  hasCoupon: boolean;
  stripePromoCode?: string;
  isLoading?: boolean;
  isLoadingButton?: boolean;
  onChangeSubscription?: (plan: Plan) => void;
  onCreateNewSubscription?: () => void;
  subscriptionItemId?: string;
}

/* Component */
const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingPeriod,
  isCurrentPlan,
  currentPlanLookupKey,
  currentPlanBillingPeriod,
  hasActiveSubscription,
  hasCoupon,
  stripePromoCode,
  isLoading = false,
  isLoadingButton = false,
  onCreateNewSubscription,
  onChangeSubscription,
}) => {
  const displayPrice = billingPeriod === "yearly" ? Math.round(plan.price / 12) : plan.price;
  const savings = billingPeriod === "yearly" ? Math.round((plan.price / 12) * 2) : 0;

  // Update the implementation to pass the full plan object
  const handleChangeSubscription = () => {
    onChangeSubscription(plan);
  };

  const handleOnClickSubscriptionAction = () => {
    trackEvent("plan_selected", { current_plan_lookup_key: currentPlanLookupKey, selected_plan_name: plan.name, selected_plan_key: plan.key, selected_plan_billing_period: billingPeriod });
    if (hasActiveSubscription) {
      handleChangeSubscription();
    } else {
      onCreateNewSubscription();
    }
  };

  const createFeatureContent = (feature) => {
    const featureContent = feature.included ? (
      <Feature key={feature.name} included={feature.included}>
        <Icon name="check-circle" />
        <FeatureContent>
          <FeatureName>
            {feature.name}
            {feature.includeTooltip && <Icon name="info" />}
          </FeatureName>
          {feature.value && <FeatureValue>{feature.value}</FeatureValue>}
        </FeatureContent>
      </Feature>
    ) : null;

    // Handle limitations feature
    if (feature.isLimitation) {
      const tooltipText = feature.limitations.map((limitation) => {
        return (
          <Styled.Limitation key={limitation.name}>
            <Styled.LimitationDescription>{limitation.description}</Styled.LimitationDescription>
          </Styled.Limitation>
        );
      });
      return (
        <Tooltip key={feature.name} label={tooltipText}>
          <Styled.LimitationFeature key={feature.name} included={false}>
            <Icon name="alert-circle" />
            <FeatureContent>
              <Styled.LimitationFeatureName>{feature.name}</Styled.LimitationFeatureName>
            </FeatureContent>
          </Styled.LimitationFeature>
        </Tooltip>
      );
    }

    return feature.includeTooltip ? (
      <Tooltip key={feature.name} label={feature.description}>
        {featureContent}
      </Tooltip>
    ) : (
      featureContent
    );
  };

  const isFreePlan = plan.key === "free";

  window.logger("%c[PlanCard] render", "background: #ed79ed; color: #b5d0eb", {
    plan,
    isCurrentPlan,
    currentPlanLookupKey,
    currentPlanBillingPeriod,
  });

  const showCurrentPlanBadge = isCurrentPlan || isFreePlan;

  return (
    <PlanCardWrapper data-testid={`plan-card-${plan.key}`}>
      <ContentContainer>
        <PricingTitle>
          <h3>{plan.name}</h3>
          {showCurrentPlanBadge && <SavingsBadge>Current plan</SavingsBadge>}
          {showCurrentPlanBadge && savings > 0 && (
            <Tooltip
              label={<Styled.SavingsLabel>You are saving ${savings} per year</Styled.SavingsLabel>}
            >
              <SavingsIcon>
                <Icon name="info" />
              </SavingsIcon>
            </Tooltip>
          )}
          {!showCurrentPlanBadge && savings > 0 && (
            <SavingsBadge>Save ${savings}/year</SavingsBadge>
          )}
        </PricingTitle>
        <PricingPrice>
          <h4>
            ${displayPrice}
            <span>
              {isFreePlan ? (
                "forever"
              ) : (
                <>
                  per month <br />
                  billed {billingPeriod}
                </>
              )}
            </span>
          </h4>
        </PricingPrice>
        <Features>{plan.features.map((feature) => createFeatureContent(feature))}</Features>
      </ContentContainer>
      <ButtonsContainer>
        {isCurrentPlan || isFreePlan ? (
          <ManageBillingActions
            hasActiveSubscription={hasActiveSubscription}
            hasCoupon={hasCoupon}
            stripePromoCode={stripePromoCode}
          />
        ) : (
          <Styled.Button
            onClick={handleOnClickSubscriptionAction}
            loading={isLoadingButton}
            disabled={isLoading}
            styleType={plan.buttonType || "secondary"}
          >
            {plan.buttonText}
          </Styled.Button>
        )}
      </ButtonsContainer>
    </PlanCardWrapper>
  );
};

export default PlanCard;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Limitation = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_Limitation;
    ${[t.mb(2)]}

    &:last-of-type {
      ${[t.mb(0)]}
    }
  `;
});

Styled.LimitationDescription = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_LimitationDescription;
    ${[t.text.xs]}
    line-height: 1.4;
  `;
});

Styled.LimitationFeatureName = styled(FeatureName)((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_FeatureName;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[400]};
  `;
});

Styled.LimitationFeature = styled(Feature)((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_Feature;
    &:hover {
      cursor: default;
    }
  `;
});

Styled.SavingsLabel = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_SavingsLabel;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[900]};
  `;
});

Styled.Button = styled(SmallButton)((props) => {
  const t: any = props.theme;
  return css`
    label: PlanCard_Button;
    min-width: 82px;
  `;
});
